import React, { useEffect } from 'react';
import styled from 'styled-components';
import { theme } from '../config/theme';
import Footer from './Footer';
import BackButton from './BackButton';
import { useBackButton } from '../contexts/BackButtonContext';

const PageContainer = styled.div`
  min-height: 100vh;
  background-color: ${theme.colors.primary.background};
  /* Use CSS custom property set by Navigation component */
  padding-top: var(--nav-height, 128px); /* fallback to 128px if CSS var not set */
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
`;

const ContentContainer = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  /* Use CSS custom property for min-height calculation */
  min-height: calc(100vh - var(--nav-height, 128px));
`;

const PageTitle = styled.h1`
  position: absolute;
  /* Position with offset from navigation height - same as other page elements */
  top: calc(var(--nav-height, 128px) + 18px);
  left: 50%;
  transform: translateX(-50%);
  z-index: 100;
  font-family: ${theme.typography.primary.fontFamily};
  font-size: 2rem;
  color: ${theme.colors.primary.text};
  margin: 0;
  text-align: center;

  @media (max-width: ${theme.breakpoints.tablet}) {
    position: static;
    transform: none;
    left: auto;
    padding: ${theme.spacing.xs} ${theme.spacing.layoutGutter};
  }
`;

/**
 * Shared page layout component that automatically handles navigation spacing
 * 
 * @param {Object} props
 * @param {React.ReactNode} props.children - Page content
 * @param {string} props.title - Optional page title (rendered as overlay on desktop, static on mobile)
 * @param {string} props.backButtonText - Optional back button text
 * @param {string} props.backButtonLink - Optional back button link
 * @param {boolean} props.includeFooter - Whether to include footer (default: true)
 * @param {string} props.className - Optional additional CSS classes
 * @param {Object} props.containerProps - Additional props for the content container
 */
const PageLayout = ({ 
  children, 
  title, 
  backButtonText, 
  backButtonLink, 
  includeFooter = true,
  className,
  containerProps = {}
}) => {
  const { setBackButton, clearBackButton } = useBackButton();

  // Set back button info in context when component mounts
  useEffect(() => {
    if (backButtonText && backButtonLink) {
      setBackButton(backButtonText, backButtonLink);
    }
    
    // Clear back button info when component unmounts
    return () => {
      clearBackButton();
    };
  }, [backButtonText, backButtonLink, setBackButton, clearBackButton]);

  return (
    <PageContainer className={className}>
      {backButtonText && backButtonLink && (
        <BackButton 
          text={backButtonText}
          to={backButtonLink}
        />
      )}

      {title && <PageTitle>{title}</PageTitle>}

      <ContentContainer {...containerProps}>
        {children}
      </ContentContainer>
      
      {includeFooter && <Footer />}
    </PageContainer>
  );
};

export default PageLayout;